import { createRouter, useRouter } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";

function DefaultErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold tracking-tight text-ink">Something went wrong</h1>
        <p className="mt-2 text-sm text-ink-muted">
          {error.message || "An unexpected error occurred."}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex h-10 items-center justify-center rounded-lg border border-input bg-background px-4 text-sm font-semibold text-ink hover:bg-muted"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      /*
        Answers stay in memory for a day rather than the default five minutes.
        Coming back to the feed after reading a listing, or reopening a
        standalone window that was backgrounded overnight, should paint from
        what we already have and refresh behind it — not blank out first.
      */
      gcTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      /*
        The default (`"online"`) *pauses* a query when the browser reports no
        connection, so it never reaches fetch — and so never reaches the service
        worker sitting behind fetch holding a cached copy of exactly this
        response. `"offlineFirst"` lets the request through: online it goes to
        the network, offline the worker answers it, and the discovery feed still
        renders on a train. With nothing cached either it fails, and the error
        state shows — which is the honest outcome.
      */
      networkMode: "offlineFirst",
    },
    mutations: {
      /*
        Deliberately not the default `"online"`. A paused mutation resumes when
        the connection returns, which for a booking or a payment means the
        action firing minutes later, from a screen the guest has long since
        left. `"always"` fails immediately and visibly instead, and the guest
        decides whether to try again.
      */
      networkMode: "always",
      retry: 0,
    },
  },
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  scrollRestoration: true,
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: DefaultErrorComponent,
});

export const getRouter = () => router;

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
